"use client";

import * as React from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { observer } from "mobx-react";

const Popover = observer(function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
});

const PopoverTrigger = observer(function PopoverTrigger({
  ...props
}: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
});

const popoverContentVariants = cva(
  "z-50 flex origin-(--transform-origin) flex-col gap-4 rounded-lg bg-popover p-2.5 text-xs text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden transition duration-(--motion-duration-base) ease-motion-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 data-[side=bottom]:data-ending-style:-translate-y-2 data-[side=bottom]:data-starting-style:-translate-y-2 data-[side=inline-end]:data-ending-style:-translate-x-2 data-[side=inline-end]:data-starting-style:-translate-x-2 data-[side=inline-start]:data-ending-style:translate-x-2 data-[side=inline-start]:data-starting-style:translate-x-2 data-[side=left]:data-ending-style:translate-x-2 data-[side=left]:data-starting-style:translate-x-2 data-[side=right]:data-ending-style:-translate-x-2 data-[side=right]:data-starting-style:-translate-x-2 data-[side=top]:data-ending-style:translate-y-2 data-[side=top]:data-starting-style:translate-y-2",
  {
    variants: {
      size: {
        sm: "w-56",
        md: "w-72",
        lg: "w-96",
        auto: "w-auto",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

/**
 * The list-picker shape (NG-2714), for a popup whose content loads or grows
 * after it opens. Base UI's popover default (`fallbackAxisSide: "end"`) lets a
 * collision walk move the popup beside its trigger once it grows, and its
 * default `clipping-ancestors` boundary extends past the fold (the app shell
 * scrolls internally, html/body clip at document height). So a list-picker is
 * pinned to the trigger's vertical axis (what `ui/dropdown-menu` and
 * `ui/select` get by default), collides against the visible viewport
 * (`documentElement`'s client rect, positioned `fixed`), and is capped to the
 * room the positioner measured so growth scrolls instead of re-placing it.
 */
const LIST_PICKER_COLLISION_AVOIDANCE = { fallbackAxisSide: "none" } as const;
const LIST_PICKER_CLASS = "max-h-(--available-height) overflow-x-hidden overflow-y-auto";

function viewportCollisionBoundary(): Element | undefined {
  return typeof document === "undefined" ? undefined : document.documentElement;
}

const PopoverContent = observer(function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  size,
  listPicker = false,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset"> &
  VariantProps<typeof popoverContentVariants> & {
    /** Content loads or grows after opening: keep the popup where it opened. */
    listPicker?: boolean;
  }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionAvoidance={listPicker ? LIST_PICKER_COLLISION_AVOIDANCE : undefined}
        collisionBoundary={listPicker ? viewportCollisionBoundary() : undefined}
        positionMethod={listPicker ? "fixed" : undefined}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            popoverContentVariants({ size }),
            listPicker && LIST_PICKER_CLASS,
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
});

const PopoverHeader = observer(function PopoverHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-1 text-xs", className)}
      {...props}
    />
  );
});

const PopoverTitle = observer(function PopoverTitle({
  className,
  ...props
}: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("font-heading text-sm font-medium", className)}
      {...props}
    />
  );
});

const PopoverDescription = observer(function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  );
});

export {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  popoverContentVariants,
};
